import type { StateCreator } from 'zustand';
import {
  AnalyticsPhaseCategories,
  AnalyticsPhases,
  trackAdformOnComplete,
  trackPhase,
} from '../../api/analytics-api';
import giosg from '../../giosg';
import { type AppStore } from '../store';
import {
  confirmPurchase,
  type ConfirmPurchaseRequest,
  type ConfirmPurchaseResponse,
} from 'api/calculator';
import type { BeneficiaryType, NamedBeneficiary } from 'dto/beneficiary';
import type { Premium } from 'dto/CoveragePremium';
import { type CoverageProduct } from 'dto/marketableProduct';
import type { ProductDocument } from 'dto/productDocument';
import type { StandardAuthenticatedState } from 'individual/types/initialState';
import { selectSelectedPremiums } from 'state/selectors/coverageSelectors';
import {
  createConfirmPurchaseRequest,
  unauthenticatedSubmit,
  validatePurchaseRequest,
} from 'state/state';
import { getInitialState, redirectTo } from 'utils';
import { focusFirstError } from 'utils/validators';

/**
 * Get initial state for Purchase slice
 * @returns Initial purchase state with default values
 */
export const getInitialPurchaseState = () => ({
  numberOfInstallments: 1,
  productDocumentsData: null as ProductDocument[] | null,
});

/**
 * Purchase Slice - Manages purchase-related state
 *
 * State:
 * - numberOfInstallments: Number of installment payments per year (1=annual, 12=monthly)
 * - productDocumentsData: Product documents (terms, conditions, etc.)
 *
 * Actions:
 * - setNumberOfInstallments: Update installment frequency
 * - setProductDocumentsData: Update product documents
 * - handleUnauthenticatedSubmit: Submit purchase for unauthenticated users
 * - handleAuthenticatedSubmit: Submit purchase for authenticated users
 */
export interface PurchaseSlice {
  // State
  numberOfInstallments: number;
  productDocumentsData: ProductDocument[] | null;

  // Actions
  setNumberOfInstallments: (installments: number) => void;
  setProductDocumentsData: (documents: ProductDocument[] | null) => void;
  handleUnauthenticatedSubmit: () => Promise<void>;
  handleAuthenticatedSubmit: () => Promise<ConfirmPurchaseResponse | undefined>;
}

/**
 * Creates the purchase slice
 */
export const createPurchaseSlice: StateCreator<
  AppStore,
  [['zustand/devtools', never]],
  [],
  PurchaseSlice
> = (set, get) => ({
  // Initial state
  ...getInitialPurchaseState(),

  // Actions
  setNumberOfInstallments: (installments) => {
    set(
      { numberOfInstallments: installments },
      false,
      'purchaseSlice/setNumberOfInstallments'
    );
  },

  setProductDocumentsData: (documents) => {
    set(
      { productDocumentsData: documents },
      false,
      'purchaseSlice/setProductDocumentsData'
    );
  },

  handleUnauthenticatedSubmit: async () => {
    // Access the full store state
    const storeState = get();
    const currentBackgroundInfo = storeState.backgroundInfoForm;
    const selectedCoverages = storeState.selectedCoverages;
    const numberOfInstallments = get().numberOfInstallments;

    // Get selected premiums using selector
    const selectedPremiumsWithUndefined = selectSelectedPremiums(storeState);
    const selectedPremiums: Record<CoverageProduct, Premium> = Object.entries(
      selectedPremiumsWithUndefined
    )
      .filter(([_, premium]) => premium !== undefined)
      .reduce<Partial<Record<CoverageProduct, Premium>>>(
        (acc, [key, value]) => {
          acc[key as CoverageProduct] = value as Premium;
          return acc;
        },
        {}
      ) as Record<CoverageProduct, Premium>;

    await unauthenticatedSubmit(
      currentBackgroundInfo,
      selectedPremiums,
      selectedCoverages,
      numberOfInstallments
    );
  },

  handleAuthenticatedSubmit: async () => {
    // Access the full store state
    const storeState = get();
    const currentBackgroundInfo = storeState.backgroundInfoForm;

    if (!currentBackgroundInfo) {
      return;
    }

    const {
      marketableProduct: { name: marketableProductName },
    } = getInitialState() as StandardAuthenticatedState;

    const selectedCoverages = storeState.selectedCoverages;
    const selectedBeneficiary = storeState.selectedBeneficiary;
    const maritalRightsExclusion = storeState.maritalRightsExclusion;
    const namedBeneficiaries = storeState.namedBeneficiaries;
    const consentForDocuments = storeState.consentForDocuments;
    const languageConfirmed = storeState.languageConfirmed;
    const numberOfInstallments = get().numberOfInstallments;

    // Get selected premiums using selector
    const selectedPremiumsWithUndefined = selectSelectedPremiums(storeState);
    const selectedPremiums: Record<CoverageProduct, Premium> = Object.entries(
      selectedPremiumsWithUndefined
    )
      .filter(([_, premium]) => premium !== undefined)
      .reduce<Partial<Record<CoverageProduct, Premium>>>(
        (acc, [key, value]) => {
          acc[key as CoverageProduct] = value as Premium;
          return acc;
        },
        {}
      ) as Record<CoverageProduct, Premium>;

    // Calculate total percentage from named beneficiaries
    const namedBeneficiariesTotalPercentage = namedBeneficiaries
      ? namedBeneficiaries.reduce(
          (sum: number, beneficiary: NamedBeneficiary) =>
            sum + (beneficiary.percentage ?? 0),
          0
        )
      : undefined;

    const request: ConfirmPurchaseRequest = createConfirmPurchaseRequest({
      backgroundInfoForm: currentBackgroundInfo,
      selectedSumInsuredPremiums: selectedPremiums,
      selectedBeneficiary: selectedBeneficiary as BeneficiaryType,
      maritalRightsExclusion,
      namedBeneficiaries,
      numberOfInstallments,
      consentForDocuments,
      languageConfirmed,
      marketableProductName,
      selectedCoverages,
    });

    const _errors = validatePurchaseRequest(
      request,
      namedBeneficiariesTotalPercentage
    );

    storeState.setErrors(_errors);

    if (!_errors) {
      // Set isConfirmingPurchase via UISlice action
      storeState.setIsConfirmingPurchase(true);

      const response = await confirmPurchase(request);
      if (
        response?.accepted &&
        response.policyNumber &&
        response.healthDeclarationId &&
        response.jobId
      ) {
        giosg.freezeGiosgChat();

        // track analytics before redirection
        trackPhase(
          AnalyticsPhaseCategories.COMPLETED,
          AnalyticsPhases.PURCHASE_CONFIRMED,
          {
            coveragePremiums: storeState.coveragePremiums,
            selectedSumInsuredPremiums: selectedPremiums,
            beneficiaryInfo: selectedBeneficiary,
            selectedCoverages: storeState.selectedCoverages,
          }
        );

        trackAdformOnComplete();

        redirectTo(window.__RHVKLSO002_CONFIG.HTS_REDIRECT_URL, {
          ...response,
          origin: window.__RHVKLSO002_INITIAL_STATE.origin,
          context: window.__RHVKLSO002_INITIAL_STATE.context,
        });
      } else if (response && !response.accepted) {
        storeState.setBlockingUWIssues(true);
      }
      return response;
    } else {
      focusFirstError(_errors);
    }
  },
});
